/* eslint-disable max-len */
import React, {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { findMatchingCustomTemplate } from './noteEditor.helpers'
import type {
  AnchoredPopoverPosition,
  CustomTemplate,
  TemplateOption,
} from './noteEditor.types'

type NoteEditorTemplateDropdownProps = {
  templateDropdownRef: React.RefObject<HTMLDivElement>
  showTemplateDropdown: boolean
  templateDropdownPosition: AnchoredPopoverPosition | null
  templateSearchQuery: string
  onTemplateSearch: (event: ChangeEvent<HTMLInputElement>) => void
  selectedTemplate: string
  customTemplates: CustomTemplate[]
  aiNoteStructure: string
  setAiNoteStructure: (value: string) => void
  setSelectedTemplate: (value: string) => void
  setTemplateButtonLabel: (value: string) => void
  setShowTemplateDropdown: (value: boolean) => void
  setTemplateDropdownPosition: (value: AnchoredPopoverPosition | null) => void
  expandedCategories: Set<string>
  setExpandedCategories: Dispatch<SetStateAction<Set<string>>>
  filteredTemplates: TemplateOption[]
  setShowCustomTemplateModal: (value: boolean) => void
  setEditingTemplateId: (value: string | null) => void
  setNoteContent: (value: string) => void
  getTemplateContent: (template: string) => string
  getTemplateDisplayName: (value: string) => string
}

const closeTemplateMenu = (
  setShowTemplateDropdown: (value: boolean) => void,
  setTemplateDropdownPosition: (value: AnchoredPopoverPosition | null) => void,
): void => {
  setShowTemplateDropdown(false)
  setTemplateDropdownPosition(null)
}

const NoteEditorTemplateDropdown = ({
  templateDropdownRef,
  showTemplateDropdown,
  templateDropdownPosition,
  templateSearchQuery,
  onTemplateSearch,
  selectedTemplate,
  customTemplates,
  aiNoteStructure,
  setAiNoteStructure,
  setSelectedTemplate,
  setTemplateButtonLabel,
  setShowTemplateDropdown,
  setTemplateDropdownPosition,
  expandedCategories,
  setExpandedCategories,
  filteredTemplates,
  setShowCustomTemplateModal,
  setEditingTemplateId,
  setNoteContent,
  getTemplateContent,
  getTemplateDisplayName,
}: NoteEditorTemplateDropdownProps): JSX.Element => (
  <div ref={templateDropdownRef} className="template-dropdown-wrapper">
    {showTemplateDropdown && (
      <div
        className="template-dropdown-menu template-dropdown-menu-toolbar"
        style={{
          position: 'fixed',
          top: templateDropdownPosition?.top,
          left: templateDropdownPosition?.left,
        }}
      >
        <div className="template-search-container">
          <i className="mgc mgc_search_2_line template-search-icon" />
          <input
            type="text"
            placeholder="Search"
            value={templateSearchQuery}
            onChange={onTemplateSearch}
            className="template-search-input"
            autoFocus
          />
        </div>
        <div className="template-list">
          {selectedTemplate ? (
            <div
              className="template-item template-clear-item"
              onClick={() => {
                const matchedStructureId = findMatchingCustomTemplate(
                  selectedTemplate,
                  customTemplates,
                )?.id
                if (
                  matchedStructureId &&
                  aiNoteStructure === matchedStructureId
                ) {
                  setAiNoteStructure('')
                }
                setSelectedTemplate('')
                setTemplateButtonLabel('Template')
                closeTemplateMenu(
                  setShowTemplateDropdown,
                  setTemplateDropdownPosition,
                )
              }}
            >
              <span>None</span>
            </div>
          ) : null}
          {filteredTemplates.map((item) => {
            const isCategory = item.isCategory
            const isExpanded = expandedCategories.has(item.value)
            return (
              <div
                key={item.value}
                className={[
                  'template-item',
                  !isCategory ? 'template-subcategory' : '',
                  isCategory ? 'template-category' : '',
                  isCategory && isExpanded ? 'template-category-expanded' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  if (isCategory) {
                    setExpandedCategories((prev) => {
                      if (prev.has(item.value)) return new Set<string>()
                      return new Set([item.value])
                    })
                    return
                  }
                  if (item.value === 'Custom_Add') {
                    closeTemplateMenu(
                      setShowTemplateDropdown,
                      setTemplateDropdownPosition,
                    )
                    setShowCustomTemplateModal(true)
                    setExpandedCategories(new Set(['Custom']))
                    return
                  }
                  setSelectedTemplate(item.value)
                  const matchedStructureId = findMatchingCustomTemplate(
                    item.value,
                    customTemplates,
                  )?.id
                  if (matchedStructureId) setAiNoteStructure(matchedStructureId)
                  setNoteContent(getTemplateContent(item.value))
                  setTemplateButtonLabel(getTemplateDisplayName(item.value))
                  closeTemplateMenu(
                    setShowTemplateDropdown,
                    setTemplateDropdownPosition,
                  )
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {isCategory && (
                      <i
                        className={`mgc ${isExpanded ? 'mgc_down_small_fill' : 'mgc_right_small_fill'}`}
                      />
                    )}
                    <span
                      className={
                        item.value === 'Custom_Add'
                          ? 'template-add-custom'
                          : undefined
                      }
                    >
                      {item.text}
                    </span>
                  </div>
                  {!isCategory && item.icon === 'custom-template-edit' && (
                    <button
                      type="button"
                      className="template-edit-icon-button"
                      title="Edit template"
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditingTemplateId(item.value)
                        setShowCustomTemplateModal(true)
                        closeTemplateMenu(
                          setShowTemplateDropdown,
                          setTemplateDropdownPosition,
                        )
                      }}
                      style={{
                        padding: 4,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 13 13"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M11.2732 0.585573C10.8982 0.210631 10.3896 0 9.85922 0C9.32889 0 8.82028 0.210631 8.44522 0.585573L7.97389 1.05757L11.2739 4.35757L11.7446 3.88624C11.9303 3.70052 12.0777 3.48002 12.1782 3.23735C12.2787 2.99468 12.3305 2.73458 12.3305 2.47191C12.3305 2.20923 12.2787 1.94913 12.1782 1.70646C12.0777 1.46379 11.9303 1.24329 11.7446 1.05757L11.2732 0.585573ZM10.3306 5.30024L7.03056 2.00024L0.970556 8.06091C0.837945 8.19354 0.745307 8.3608 0.703223 8.54357L0.0172228 11.5136C-0.00833782 11.6238 -0.00540589 11.7388 0.0257423 11.8476C0.0568906 11.9564 0.11523 12.0555 0.195262 12.1355C0.275295 12.2156 0.374386 12.2739 0.483199 12.3051C0.592012 12.3362 0.706964 12.3391 0.817223 12.3136L3.78789 11.6282C3.97043 11.586 4.13744 11.4934 4.26989 11.3609L10.3306 5.30024Z"
                          fill="#97C9F9"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )}
  </div>
)

export default NoteEditorTemplateDropdown
